package nl.volleyplanner.web.controller.thuisprogramma.version_selector

import kotlinx.browser.document
import kotlinx.coroutines.flow.StateFlow
import kotlinx.dom.addClass
import kotlinx.dom.clear
import kotlinx.dom.removeClass
import kotlinx.html.*
import kotlinx.html.dom.create
import kotlinx.html.js.div
import nl.volleyplanner.api.auth.AuthLevel
import nl.volleyplanner.api.planner.CreatePlanningProblemVersionBaseVersionType
import nl.volleyplanner.api.planner.CreatePlanningProblemVersionRequest
import nl.volleyplanner.api.planner.PlanningProblemVersion
import nl.volleyplanner.web.ApiDataStateFlow
import nl.volleyplanner.web.VolleyPlannerApplication
import nl.volleyplanner.web.base.component
import nl.volleyplanner.web.base.component.*
import nl.volleyplanner.web.base.icon
import nl.volleyplanner.web.base.spinner
import nl.volleyplanner.web.controller.thuisprogramma.ThuisprogrammaDataManager
import nl.volleyplanner.web.service.WebUserAndOrganisationService
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLFormElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.Event
import org.w3c.xhr.FormData

class CreateVersionModalComponent(
    private val thuisprogrammaDataManager: ThuisprogrammaDataManager,
    private val planningProblemVersionsState: StateFlow<List<PlanningProblemVersion>?>
) : ModalComponent("Nieuwe versie", ModalSize.Large) {
    override val footerButtons = listOf(
        BootstrapButton("Annuleren", ButtonVariant.Secondary, null, mapOf("data-bs-dismiss" to "modal")),
        BootstrapButton("Opslaan", ButtonVariant.Primary, {
            it.preventDefault()

            // TODO: kill me now. Need to completely revamp the Modal component to properly submit forms through modal buttons
            //  super dirty workaround. Firing a submit event to trigger the form submit functions
            //  (cannot call submit directly as this would not use the custom submit functions and events)
            //  Yes kill me more: this is directly copied from another place where we already do this, making the required refactor only bigger....
            val formElement = document.getElementById("vp-create-problem-version-form")!! as HTMLFormElement
            formElement.dispatchEvent(Event("submit"))
        }
        )
    )

    val plannerConfigurationsState = ApiDataStateFlow(
        "PlannerConfigurationsData",
        AuthLevel.AdminUser,
        contextFlow = WebUserAndOrganisationService.selectedOrganisationState
    ) { organisationContext ->
        organisationContext?.volleyplannerApiClient?.organisation?.plannerConfiguration?.listPlannerConfigurations()
    }

    override suspend fun getState() = plannerConfigurationsState.subscribe()

    override fun FlowContent.renderBody() {
        val plannerConfigurations = plannerConfigurationsState.value
        if (plannerConfigurations == null) {
            spinner()
            return
        }
        validatingForm(
            customValidityFunction = { formElement ->
                val nameInputElement = formElement.querySelector("#vp-version-name") as HTMLInputElement
                val nameValue = nameInputElement.value.trim()

                if (planningProblemVersionsState.value == null) {
                    nameInputElement.setCustomValidity("Kan veld niet valideren, probeer opnieuw")
                } else if(planningProblemVersionsState.value!!.any { it.name == nameValue }) {
                    nameInputElement.setCustomValidity("Naam $nameValue bestaat al. Kies een andere naam.")
                } else {
                    nameInputElement.setCustomValidity("")
                }

            },
            validSubmitFunction = { event, formElement ->
                event.preventDefault()
                val statusElement = document.getElementById("vp-create-version-form-status")!! as HTMLElement

                statusElement.removeClass("d-none")
                statusElement.clear()

                statusElement.append(document.create.div(classes = "alert alert-secondary d-flex align-items-center") {
                    role = "alert"
                    spinner()
                    +"Gegevens opslaan..."
                })

                val formData = FormData(formElement)
                val versionName = formData.get("vp-version-name").trim()
                val baseVersion = formData.get("vp-base-version") as String
                val plannerConfigurationIdentifier = formData.get("vp-planner-configuration") as String

                val baseVersionType = when(baseVersion) {
                    "Blank" -> CreatePlanningProblemVersionBaseVersionType.Blank
                    "Published" -> CreatePlanningProblemVersionBaseVersionType.Published
                    else -> CreatePlanningProblemVersionBaseVersionType.Version
                }

                val createRequest = CreatePlanningProblemVersionRequest(
                    versionName,
                    plannerConfigurationIdentifier,
                    baseVersionType,
                    baseVersion.takeIf { baseVersionType == CreatePlanningProblemVersionBaseVersionType.Version }
                )
                VolleyPlannerApplication.volleyplannerApiClient
                    .organisation
                    .plannerConfiguration
                    .createProblemVersion(thuisprogrammaDataManager.problemSlug, createRequest)
                thuisprogrammaDataManager.problemData.problemVersionsDataFlow.refresh()
                statusElement.addClass("d-none")
                hide()
            },
            onSubmitErrorFunction = { throwable, formElement ->
                val statusElement = document.getElementById("vp-create-version-form-status")!! as HTMLElement
                statusElement.removeClass("d-none")
                statusElement.clear()

                statusElement.append(document.create.div (classes = "alert alert-danger d-flex align-items-center") {
                    role = "alert"
                    icon("bi-exclamation-triangle")
                    +"Fout bij opslaan gegevens: ${throwable.message}"
                })
            }
        ) {
            id = "vp-create-problem-version-form"

            div(classes = "d-none") {
                id = "vp-create-version-form-status"
            }
            div(classes = "p-3 mb-2") {
                fieldSet {
                    // Heading
                    legend(classes = "h6 mb-4") {
                        +"Nieuwe versie"
                    }

                    // Version Name Input
                    div(classes = "row g-3 mb-4 align-items-center") {
                        label(classes = "col-12 col-lg-4 col-form-label") {
                            htmlFor = "vp-version-name"
                            +"Naam"
                        }
                        div(classes = "col-12 col-lg-8 col-xl-6") {
                            input(type = InputType.text, classes = "form-control") {
                                id = "vp-version-name"
                                name = "vp-version-name"
                                required = true
                            }
                            div(classes = "invalid-feedback") {
                                +"Een naam is verplicht."
                            }
                        }
                    }

                    // Planner Configuration Select
                    div(classes = "row g-3 mb-4 align-items-center") {
                        label(classes = "col-12 col-lg-4 col-form-label") {
                            htmlFor = "vp-planner-configuration"
                            +"Planner configuratie"
                        }
                        div(classes = "col-12 col-lg-8 col-xl-6") {
                            select(classes = "form-select") {
                                id = "vp-planner-configuration"
                                name = "vp-planner-configuration"
                                required = true

                                plannerConfigurations.forEach { configuration ->
                                    option {
                                        value = configuration.identifier
                                        +configuration.name
                                    }
                                }
                            }
                            div(classes = "form-text") {
                                +"De configuratie die wordt gebruikt bij het draaien van de planner voor deze versie en om scores/statistieken te berekenen. "
                                +"Momenteel is enkel de 'standaard' configuratie beschikbaar."
                            }
                            div(classes = "invalid-feedback") {
                                +"Een planner configuratie is verplicht."
                            }
                        }
                    }

                    // Base Version Select
                    div(classes = "row g-3 mb-4 align-items-center") {
                        label(classes = "col-12 col-lg-4 col-form-label") {
                            htmlFor = "vp-base-version"
                            +"Basis versie"
                        }
                        div(classes = "col-12 col-lg-8 col-xl-6") {
                            component(
                                ListeningComponent(
                                    "CreateVersionBaseVersionSelect",
                                    listOf(planningProblemVersionsState)
                                ) {
                                    select(classes = "form-select") {
                                        id = "vp-base-version"
                                        name = "vp-base-version"
                                        required = true

                                        option {
                                            value = "Blank"
                                            +"Lege versie"
                                        }
                                        option {
                                            value = "Published"
                                            selected =
                                                thuisprogrammaDataManager.problemData.selectedProblemVersionSlugState.value == null

                                            +"Gepubliceerde versie"
                                        }
                                        planningProblemVersionsState.value?.forEach { problemVersion ->
                                            option {
                                                value = problemVersion.slug
                                                selected =
                                                    thuisprogrammaDataManager.problemData.selectedProblemVersionSlugState.value == problemVersion.slug

                                                +problemVersion.name
                                            }
                                        }
                                    }
                                })
                            div(classes = "form-text") {
                                +"De versie die als startpunt wordt gebruikt bij het maken van deze nieuwe versie."
                                +"Selecteer 'Lege versie' om alles leeg te laten en helemaal schoon te beginnen."
                            }
                            div(classes = "invalid-feedback") {
                                +"Een basis versie is verplicht."
                            }
                        }
                    }
                }
            }
        }
    }
}